import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Brain,
  Mail,
  MessageSquare,
  MapPin,
  Settings,
  Save,
  Eye,
  EyeOff,
  Loader2,
  Check,
  GitBranch,
  Copy,
  ExternalLink,
  Bot,
  Wrench,
  Globe,
  Database,
  Target,
  FileText,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Code
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSelector } from '../components/LanguageSelector';
import { SeedSection } from '../components/settings/SeedSection';
import { CoverageMapSection } from '../components/settings/CoverageMapSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AIProviderSection = ({ t }) => {
  const [config, setConfig] = useState({
    provider: 'openai',
    model: 'gpt-5.2',
    api_key: '',
    is_active: false
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/ai-provider`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching AI config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/ai-provider`, null, {
        params: {
          provider: config.provider,
          model: config.model,
          api_key: config.api_key || undefined,
          is_active: config.is_active
        }
      });
      toast.success(t('ai.messages.saved'));
    } catch (error) {
      toast.error(t('ai.messages.error'));
    }
    setSaving(false);
  };

  const models = {
    openai: ['gpt-5.2', 'gpt-5.1', 'gpt-4o', 'gpt-4o-mini'],
    gemini: ['gemini-3-flash-preview', 'gemini-2.5-pro', 'gemini-2.5-flash'],
    claude: ['claude-sonnet-4-5-20250929', 'claude-opus-4-5-20251101', 'claude-haiku-4-5-20251001']
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('ai.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('ai.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="ai-active">{t('ai.enabled')}</Label>
          <Switch
            id="ai-active"
            checked={config.is_active}
            onCheckedChange={(checked) => setConfig({...config, is_active: checked})}
            data-testid="ai-active-switch"
          />
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('ai.provider')}</Label>
            <Select
              value={config.provider}
              onValueChange={(value) => setConfig({...config, provider: value, model: models[value][0]})}
            >
              <SelectTrigger data-testid="ai-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="gemini">Google Gemini</SelectItem>
                <SelectItem value="claude">Anthropic Claude</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('ai.model')}</Label>
            <Select
              value={config.model}
              onValueChange={(value) => setConfig({...config, model: value})}
            >
              <SelectTrigger data-testid="ai-model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {models[config.provider]?.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('ai.apiKey')}</Label>
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={config.api_key}
              onChange={(e) => setConfig({...config, api_key: e.target.value})}
              placeholder="sk-..."
              className="pr-10"
              data-testid="ai-api-key"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {t('ai.apiKeyHint')}
          </p>
        </div>

        <Button 
          onClick={handleSave} 
          className="w-full bg-[#E63946] hover:bg-[#C92A36]"
          disabled={saving}
          data-testid="save-ai-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('ai.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

const EmailProviderSection = ({ t }) => {
  const [config, setConfig] = useState({
    provider_type: 'imap_smtp',
    is_active: false,
    enable_auto_monitor: false,
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    webhook_url: '',
    o365_subscription_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // System webhook URL that Office 365 should call
  const systemWebhookUrl = `${API_URL}/api/webhooks/email/inbound`;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/email-provider`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save configuration
      await axios.put(`${API_URL}/api/config/email/provider`, { value: config });
      
      // If auto monitor setting changed, trigger the monitor start/stop
      if (config.enable_auto_monitor) {
        try {
          // Call check-now to start the monitor if it's not running
          await axios.post(`${API_URL}/api/email-monitor/check-now`);
        } catch (e) {
          // Monitor might already be running, that's ok
          console.log('Monitor check triggered');
        }
      }
      
      toast.success(t('email.messages.saved'));
    } catch (error) {
      toast.error(t('email.messages.error'));
    }
    setSaving(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('whatsapp.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('email.messages.error'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('email.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('email.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>{t('email.enabled')}</Label>
          <Switch
            checked={config.is_active}
            onCheckedChange={(checked) => setConfig({...config, is_active: checked})}
            data-testid="email-active-switch"
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>{t('ai.provider')}</Label>
          <Select
            value={config.provider_type}
            onValueChange={(value) => setConfig({...config, provider_type: value})}
          >
            <SelectTrigger data-testid="email-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="imap_smtp">IMAP/SMTP</SelectItem>
              <SelectItem value="o365_webhook">Office 365 Webhook</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {config.provider_type === 'imap_smtp' ? (
          <>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t('email.imapConfig')}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('email.host')}</Label>
                  <Input
                    value={config.imap_host}
                    onChange={(e) => setConfig({...config, imap_host: e.target.value})}
                    placeholder="imap.example.com"
                    data-testid="imap-host"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('email.port')}</Label>
                  <Input
                    type="number"
                    value={config.imap_port}
                    onChange={(e) => setConfig({...config, imap_port: parseInt(e.target.value)})}
                    data-testid="imap-port"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('email.username')}</Label>
                  <Input
                    value={config.imap_username}
                    onChange={(e) => setConfig({...config, imap_username: e.target.value})}
                    data-testid="imap-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('email.password')}</Label>
                  <Input
                    type="password"
                    value={config.imap_password}
                    onChange={(e) => setConfig({...config, imap_password: e.target.value})}
                    data-testid="imap-password"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t('email.smtpConfig')}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('email.host')}</Label>
                  <Input
                    value={config.smtp_host}
                    onChange={(e) => setConfig({...config, smtp_host: e.target.value})}
                    placeholder="smtp.example.com"
                    data-testid="smtp-host"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('email.port')}</Label>
                  <Input
                    type="number"
                    value={config.smtp_port}
                    onChange={(e) => setConfig({...config, smtp_port: parseInt(e.target.value)})}
                    data-testid="smtp-port"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>{t('email.fromEmail')}</Label>
                  <Input
                    type="email"
                    value={config.smtp_from_email}
                    onChange={(e) => setConfig({...config, smtp_from_email: e.target.value})}
                    placeholder="noreply@example.com"
                    data-testid="smtp-from-email"
                  />
                </div>
              </div>
            </div>

            {/* Auto Monitor Section */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-green-500 text-white flex items-center justify-center">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100">{t('email.autoMonitor.title')}</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">{t('email.autoMonitor.description')}</p>
                  </div>
                </div>
                <Switch
                  checked={config.enable_auto_monitor || false}
                  onCheckedChange={(checked) => setConfig({...config, enable_auto_monitor: checked})}
                  data-testid="auto-monitor-switch"
                />
              </div>
              {config.enable_auto_monitor && (
                <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-green-200 dark:border-green-700">
                  <div className="flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span>{t('email.autoMonitor.activeMessage')}</span>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('email.autoMonitor.interval')}
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Section 1: System Webhook URL (for Office 365 to call) */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100">{t('email.o365.systemUrlTitle')}</h4>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('email.o365.systemUrlDesc')}
              </p>
              <div className="flex gap-2">
                <Input
                  value={systemWebhookUrl}
                  readOnly
                  className="bg-white dark:bg-gray-800 font-mono text-sm"
                  data-testid="system-webhook-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(systemWebhookUrl)}
                  className="shrink-0"
                  data-testid="copy-webhook-url"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('email.o365.systemUrlCopyHint')}
              </p>
            </div>

            {/* Section 2: Office 365 Subscription URL (optional, for outbound) */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-gray-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{t('email.o365.subscriptionTitle')}</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('email.o365.subscriptionDesc')}
              </p>
              <div className="space-y-2">
                <Label>{t('email.o365.subscriptionUrl')}</Label>
                <Input
                  value={config.o365_subscription_url || ''}
                  onChange={(e) => setConfig({...config, o365_subscription_url: e.target.value})}
                  placeholder="https://graph.microsoft.com/v1.0/subscriptions/..."
                  data-testid="o365-subscription-url"
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-2 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                {t('email.o365.instructionsTitle')}
              </h4>
              <ol className="text-sm text-amber-800 dark:text-amber-200 space-y-2 list-decimal list-inside">
                <li>{t('email.o365.step1')} <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-[#E63946] underline">{t('email.o365.step1Link')}</a> {t('email.o365.step1End')}</li>
                <li>{t('email.o365.step2')}</li>
                <li>{t('email.o365.step3')}</li>
                <li>{t('email.o365.step4')} <a href="/api-tokens" className="text-[#E63946] underline">{t('email.o365.step4Link')}</a> {t('email.o365.step4End')}</li>
                <li>{t('email.o365.step5')} <code className="bg-amber-100 dark:bg-amber-800 px-1">Authorization: Bearer YOUR_TOKEN</code></li>
                <li>{t('email.o365.step6')}</li>
              </ol>
            </div>
          </>
        )}

        <Button 
          onClick={handleSave}
          className="w-full bg-[#E63946] hover:bg-[#C92A36]"
          disabled={saving}
          data-testid="save-email-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('email.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

const WhatsAppSection = ({ t }) => {
  const [config, setConfig] = useState({
    is_active: false,
    phone_number_id: '',
    business_account_id: '',
    access_token: '',
    webhook_verify_token: '',
    api_version: 'v21.0'
  });
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  // Webhook URL for Meta to call
  const webhookUrl = `${API_URL}/api/whatsapp/webhook`;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/whatsapp`);
      setConfig(prev => ({...prev, ...response.data}));
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/whatsapp/settings`, { value: config });
      toast.success(t('whatsapp.messages.saved'));
    } catch (error) {
      toast.error(t('whatsapp.messages.error'));
    }
    setSaving(false);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'url') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
    toast.success(t('whatsapp.copied'));
  };

  const generateVerifyToken = () => {
    const token = 'esc_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
    setConfig({...config, webhook_verify_token: token});
    toast.info(t('whatsapp.tokenGenerated'));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#25D366]" />
          <CardTitle>{t('whatsapp.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('whatsapp.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>{t('whatsapp.enabled')}</Label>
          <Switch
            checked={config.is_active}
            onCheckedChange={(checked) => setConfig({...config, is_active: checked})}
            data-testid="whatsapp-active-switch"
          />
        </div>

        <Separator />

        {/* Webhook Configuration Section */}
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 space-y-4">
          <h4 className="font-medium text-green-900 dark:text-green-100 flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            {t('whatsapp.webhookConfig')}
          </h4>
          
          {/* Webhook URL */}
          <div className="space-y-2">
            <Label className="text-green-800 dark:text-green-200">{t('whatsapp.webhookUrl')}</Label>
            <div className="flex gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="bg-white dark:bg-gray-800 font-mono text-sm"
                data-testid="whatsapp-webhook-url"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(webhookUrl, 'url')}
                className="shrink-0"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-green-700 dark:text-green-300">
              {t('whatsapp.webhookUrlHint')}
            </p>
          </div>

          {/* Verify Token */}
          <div className="space-y-2">
            <Label className="text-green-800 dark:text-green-200">{t('whatsapp.verifyToken')}</Label>
            <div className="flex gap-2">
              <Input
                value={config.webhook_verify_token}
                onChange={(e) => setConfig({...config, webhook_verify_token: e.target.value})}
                placeholder={t('whatsapp.verifyToken')}
                className="font-mono text-sm"
                data-testid="whatsapp-verify-token"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(config.webhook_verify_token, 'token')}
                className="shrink-0"
                disabled={!config.webhook_verify_token}
              >
                {copiedToken ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                onClick={generateVerifyToken}
                className="shrink-0"
              >
                {t('whatsapp.generate')}
              </Button>
            </div>
            <p className="text-xs text-green-700 dark:text-green-300">
              {t('whatsapp.verifyTokenHint')}
            </p>
          </div>
        </div>

        {/* API Credentials Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">{t('whatsapp.apiCredentials')}</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('whatsapp.phoneNumberId')}</Label>
              <Input
                value={config.phone_number_id}
                onChange={(e) => setConfig({...config, phone_number_id: e.target.value})}
                placeholder="123456789..."
                data-testid="whatsapp-phone-id"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('whatsapp.businessAccountId')}</Label>
              <Input
                value={config.business_account_id}
                onChange={(e) => setConfig({...config, business_account_id: e.target.value})}
                placeholder="123456789..."
                data-testid="whatsapp-account-id"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>{t('whatsapp.accessToken')}</Label>
            <div className="flex gap-2">
              <Input
                type={showToken ? "text" : "password"}
                value={config.access_token}
                onChange={(e) => setConfig({...config, access_token: e.target.value})}
                placeholder="EAAx..."
                data-testid="whatsapp-token"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('whatsapp.accessTokenHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t('whatsapp.apiVersion')}</Label>
            <Select 
              value={config.api_version} 
              onValueChange={(value) => setConfig({...config, api_version: value})}
            >
              <SelectTrigger data-testid="whatsapp-api-version">
                <SelectValue placeholder={t('whatsapp.selectVersion')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="v21.0">v21.0 ({t('whatsapp.recommended')})</SelectItem>
                <SelectItem value="v20.0">v20.0</SelectItem>
                <SelectItem value="v19.0">v19.0</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-2 flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            {t('whatsapp.instructionsTitle')}
          </h4>
          <ol className="text-sm text-amber-800 dark:text-amber-200 space-y-1 list-decimal list-inside">
            <li>{t('whatsapp.instruction1')} <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#E63946] underline">{t('whatsapp.instruction1Link')}</a></li>
            <li>{t('whatsapp.instruction2')}</li>
            <li>{t('whatsapp.instruction3')}</li>
            <li>{t('whatsapp.instruction4')}</li>
            <li>{t('whatsapp.instruction5')}</li>
          </ol>
        </div>

        <Button 
          onClick={handleSave}
          className="w-full bg-[#25D366] hover:bg-[#1DA851]"
          disabled={saving}
          data-testid="save-whatsapp-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('whatsapp.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

const MapsSection = ({ t }) => {
  const [config, setConfig] = useState({
    provider: 'google',
    api_key: '',
    is_active: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/maps`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching maps config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/maps/provider`, { value: config });
      toast.success(t('maps.messages.saved'));
    } catch (error) {
      toast.error(t('maps.messages.error'));
    }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('maps.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('maps.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>{t('maps.enabled')}</Label>
          <Switch
            checked={config.is_active}
            onCheckedChange={(checked) => setConfig({...config, is_active: checked})}
            data-testid="maps-active-switch"
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>{t('maps.apiKey')}</Label>
          <Input
            type="password"
            value={config.api_key}
            onChange={(e) => setConfig({...config, api_key: e.target.value})}
            placeholder="AIza..."
            data-testid="maps-api-key"
          />
          <p className="text-xs text-muted-foreground">
            {t('maps.apiKeyHint')} <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#E63946]">{t('maps.apiKeyLink')}</a>
          </p>
        </div>

        <Button 
          onClick={handleSave}
          className="w-full bg-[#E63946] hover:bg-[#C92A36]"
          disabled={saving}
          data-testid="save-maps-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('maps.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

const WorkflowSection = ({ t }) => {
  const [config, setConfig] = useState({
    is_active: false,
    default_flow_id: '',
    auto_create_agent_queue: true,
    auto_execute_tasks: true,
    max_task_retries: 3,
    task_timeout_seconds: 60,
    // Medication filter settings
    medication_filter: {
      include_all: true,
      include_keywords: '',
      exclude_keywords: ''
    }
  });
  const [flows, setFlows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
    fetchFlows();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/workflows`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching workflow config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlows = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/flows?limit=100&status=active`);
      setFlows(response.data.data || []);
    } catch (error) {
      console.error('Error fetching flows:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/workflows`, config);
      toast.success(t('workflows.messages.saved'));
    } catch (error) {
      toast.error(t('workflows.messages.error'));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('workflows.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('workflows.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>{t('workflows.engineEnabled')}</Label>
            <p className="text-xs text-muted-foreground">{t('workflows.engineEnabledHint')}</p>
          </div>
          <Switch
            checked={config.is_active}
            onCheckedChange={(checked) => setConfig({...config, is_active: checked})}
            data-testid="workflow-active-switch"
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>{t('workflows.defaultFlow')}</Label>
          <Select
            value={config.default_flow_id || 'none'}
            onValueChange={(value) => setConfig({...config, default_flow_id: value === 'none' ? null : value})}
          >
            <SelectTrigger data-testid="default-flow-select">
              <SelectValue placeholder={t('workflows.selectDefaultFlow')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('workflows.noDefaultFlow')}</SelectItem>
              {flows.map(flow => (
                <SelectItem key={flow.id} value={flow.id}>
                  {flow.name} {flow.is_fallback && `(${t('workflows.fallback')})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('workflows.defaultFlowHint')}
          </p>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-4">
          <h4 className="font-medium text-foreground">{t('workflows.automationOptions')}</h4>
          
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{t('workflows.autoCreateQueue')}</Label>
              <p className="text-xs text-muted-foreground">{t('workflows.autoCreateQueueHint')}</p>
            </div>
            <Switch
              checked={config.auto_create_agent_queue}
              onCheckedChange={(checked) => setConfig({...config, auto_create_agent_queue: checked})}
              data-testid="auto-queue-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{t('workflows.autoExecuteTasks')}</Label>
              <p className="text-xs text-muted-foreground">{t('workflows.autoExecuteTasksHint')}</p>
            </div>
            <Switch
              checked={config.auto_execute_tasks}
              onCheckedChange={(checked) => setConfig({...config, auto_execute_tasks: checked})}
              data-testid="auto-execute-switch"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('workflows.maxRetries')}</Label>
            <Input
              type="number"
              min="0"
              max="10"
              value={config.max_task_retries}
              onChange={(e) => setConfig({...config, max_task_retries: parseInt(e.target.value) || 3})}
              data-testid="max-retries"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('workflows.taskTimeout')}</Label>
            <Input
              type="number"
              min="10"
              max="300"
              value={config.task_timeout_seconds}
              onChange={(e) => setConfig({...config, task_timeout_seconds: parseInt(e.target.value) || 60})}
              data-testid="task-timeout"
            />
          </div>
        </div>

        <Separator />

        {/* Medication Filter Section */}
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm">
              💊
            </div>
            <div>
              <h4 className="font-medium text-purple-900 dark:text-purple-100">{t('workflows.medicationFilter.title')}</h4>
              <p className="text-sm text-purple-700 dark:text-purple-300">{t('workflows.medicationFilter.description')}</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-purple-200 dark:border-purple-700">
            <div>
              <Label className="text-sm">{t('workflows.medicationFilter.includeAll')}</Label>
              <p className="text-xs text-muted-foreground">{t('workflows.medicationFilter.includeAllHint')}</p>
            </div>
            <Switch
              checked={config.medication_filter?.include_all ?? true}
              onCheckedChange={(checked) => setConfig({
                ...config, 
                medication_filter: { ...config.medication_filter, include_all: checked }
              })}
              data-testid="include-all-medications-switch"
            />
          </div>

          {!config.medication_filter?.include_all && (
            <div className="space-y-4 p-3 bg-white dark:bg-gray-800 rounded border border-purple-200 dark:border-purple-700">
              <div className="space-y-2">
                <Label className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {t('workflows.medicationFilter.includeKeywords')}
                </Label>
                <Input
                  value={config.medication_filter?.include_keywords || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    medication_filter: { ...config.medication_filter, include_keywords: e.target.value }
                  })}
                  placeholder={t('workflows.medicationFilter.includeKeywordsPlaceholder')}
                  data-testid="include-keywords-input"
                />
                <p className="text-xs text-muted-foreground">{t('workflows.medicationFilter.includeKeywordsHint')}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <EyeOff className="h-4 w-4" />
                  {t('workflows.medicationFilter.excludeKeywords')}
                </Label>
                <Input
                  value={config.medication_filter?.exclude_keywords || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    medication_filter: { ...config.medication_filter, exclude_keywords: e.target.value }
                  })}
                  placeholder={t('workflows.medicationFilter.excludeKeywordsPlaceholder')}
                  data-testid="exclude-keywords-input"
                />
                <p className="text-xs text-muted-foreground">{t('workflows.medicationFilter.excludeKeywordsHint')}</p>
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded text-sm text-amber-800 dark:text-amber-200">
                <strong>{t('workflows.medicationFilter.example')}:</strong><br/>
                {t('workflows.medicationFilter.exampleText')}
              </div>
            </div>
          )}
        </div>

        <Button 
          onClick={handleSave}
          className="w-full bg-[#E63946] hover:bg-[#C92A36]"
          disabled={saving}
          data-testid="save-workflow-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('workflows.save')}
        </Button>
      </CardContent>
    </Card>
  );
};


// ============== EMAIL TEMPLATES SECTION ==============
const EmailTemplatesSection = ({ t }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      TextStyle,
      Color,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setEditedContent(editor.getHTML());
    },
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Lock body scroll when preview modal is open
  useEffect(() => {
    if (showPreview) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showPreview]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email-templates`);
      setTemplates(response.data.templates || []);
      
      // Select first template by default
      if (response.data.templates?.length > 0 && !selectedTemplateId) {
        const firstTemplate = response.data.templates[0];
        setSelectedTemplateId(firstTemplate.id);
        loadTemplate(firstTemplate);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error(t('emailTemplates.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (template) => {
    setCurrentTemplate(template);
    setEditedSubject(template.subject || '');
    setEditedContent(template.html_content || '');
    if (editor) {
      editor.commands.setContent(template.html_content || '');
    }
    setShowPreview(false);
  };

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      loadTemplate(template);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplateId) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/email-templates/${selectedTemplateId}`, {
        subject: editedSubject,
        html_content: editedContent
      });
      
      toast.success(t('emailTemplates.messages.saved'));
      
      // Refresh templates
      await fetchTemplates();
    } catch (error) {
      toast.error(t('emailTemplates.messages.saveError'));
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!selectedTemplateId) return;
    
    if (!window.confirm(t('emailTemplates.confirmReset'))) return;
    
    try {
      await axios.post(`${API_URL}/api/email-templates/${selectedTemplateId}/reset`);
      toast.success(t('emailTemplates.messages.reset'));
      await fetchTemplates();
    } catch (error) {
      toast.error(t('emailTemplates.messages.resetError'));
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplateId) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/email-templates/${selectedTemplateId}/preview`);
      setPreviewHtml(response.data.html_content);
      setShowPreview(true);
    } catch (error) {
      toast.error(t('emailTemplates.messages.previewError'));
    }
  };

  const insertPlaceholder = (placeholder) => {
    // Insert at cursor position or at end
    const textarea = document.getElementById('template-editor');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = editedContent.substring(0, start) + placeholder + editedContent.substring(end);
      setEditedContent(newContent);
      
      // Set cursor after inserted placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      setEditedContent(editedContent + placeholder);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('emailTemplates.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('emailTemplates.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template Selector */}
        <div className="space-y-2">
          <Label>{t('emailTemplates.selectTemplate')}</Label>
          <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
            <SelectTrigger data-testid="template-selector">
              <SelectValue placeholder={t('emailTemplates.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {templates.map(template => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name} {template.is_customized && '✏️'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentTemplate && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Editor Section - 2 columns */}
            <div className="lg:col-span-2 space-y-4">
              {/* Subject */}
              <div className="space-y-2">
                <Label>{t('emailTemplates.subject')}</Label>
                <Input
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  placeholder={t('emailTemplates.subjectPlaceholder')}
                  data-testid="template-subject"
                />
              </div>

              {/* Editor Toggle and Preview */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('emailTemplates.htmlContent')}</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={showCodeEditor ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowCodeEditor(!showCodeEditor)}
                      data-testid="toggle-code-editor"
                    >
                      <Code className="h-4 w-4 mr-1" />
                      {showCodeEditor ? t('emailTemplates.visualMode') : t('emailTemplates.codeMode')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      data-testid="preview-template"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {t('emailTemplates.preview')}
                    </Button>
                  </div>
                </div>
                
                {/* WYSIWYG Editor */}
                {!showCodeEditor ? (
                  <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900" data-testid="wysiwyg-editor">
                    {/* TipTap Toolbar */}
                    <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-800 border-b">
                      <button
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Bold"
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Italic"
                      >
                        <em>I</em>
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().toggleStrike().run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('strike') ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Strikethrough"
                      >
                        <s>S</s>
                      </button>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />
                      <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 1 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Heading 1"
                      >
                        H1
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Heading 2"
                      >
                        H2
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Heading 3"
                      >
                        H3
                      </button>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />
                      <button
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Bullet List"
                      >
                        • List
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Numbered List"
                      >
                        1. List
                      </button>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center" />
                      <button
                        onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive({ textAlign: 'left' }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Align Left"
                      >
                        ←
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive({ textAlign: 'center' }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Align Center"
                      >
                        ↔
                      </button>
                      <button
                        onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                        className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${editor?.isActive({ textAlign: 'right' }) ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                        title="Align Right"
                      >
                        →
                      </button>
                    </div>
                    {/* Editor Content */}
                    <EditorContent 
                      editor={editor} 
                      className="p-4 min-h-[350px] prose prose-sm max-w-none dark:prose-invert"
                    />
                  </div>
                ) : (
                  /* Code Editor */
                  <Textarea
                    id="template-editor"
                    value={editedContent}
                    onChange={(e) => {
                      setEditedContent(e.target.value);
                      if (editor) {
                        editor.commands.setContent(e.target.value);
                      }
                    }}
                    className="font-mono text-sm min-h-[400px] bg-gray-50 dark:bg-gray-900"
                    placeholder={t('emailTemplates.contentPlaceholder')}
                    data-testid="template-code-editor"
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-[#E63946] hover:bg-[#C92A36]"
                  disabled={saving}
                  data-testid="save-template"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {t('emailTemplates.save')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  data-testid="reset-template"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('emailTemplates.reset')}
                </Button>
              </div>
            </div>

            {/* Placeholders Panel - 1 column */}
            <div className="space-y-2">
              <button
                onClick={() => setShowPlaceholders(!showPlaceholders)}
                className="flex items-center justify-between w-full p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-800 dark:text-blue-200">
                    {t('emailTemplates.placeholders')}
                  </span>
                </div>
                {showPlaceholders ? (
                  <ChevronDown className="h-4 w-4 text-blue-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-blue-600" />
                )}
              </button>
              
              {showPlaceholders && currentTemplate.placeholders && (
                <div className="border rounded-lg p-3 space-y-2 max-h-[500px] overflow-y-auto bg-white dark:bg-gray-800">
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('emailTemplates.placeholdersHint')}
                  </p>
                  {currentTemplate.placeholders.map((ph, idx) => (
                    <div
                      key={idx}
                      className="p-2 bg-gray-50 dark:bg-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                      onClick={() => insertPlaceholder(ph.key)}
                      title={t('emailTemplates.clickToInsert')}
                    >
                      <div className="font-mono text-sm text-blue-600 dark:text-blue-400">
                        {ph.key}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {ph.description}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* Preview Modal - Using Portal to render outside Card to avoid transform containment issues */}
      {showPreview && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ 
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowPreview(false)}
          data-testid="preview-modal-backdrop"
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 dark:bg-gray-900">
              <h3 className="font-semibold text-lg">{t('emailTemplates.previewTitle')}</h3>
              <button 
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                data-testid="preview-modal-close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100 dark:bg-gray-950">
              <div
                className="bg-white rounded-lg shadow-lg mx-auto"
                style={{ maxWidth: '700px' }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
};


// ============== AGENT CONFIGURATION SECTION ==============
const AgentSection = ({ t }) => {
  const [config, setConfig] = useState({
    is_active: true,
    agent_name: 'Agente Especialistas en Casa',
    personality: 'Eres un agente de soporte amigable y profesional para un servicio de atención médica domiciliaria. Respondes siempre en español y ayudas a los pacientes con sus consultas sobre servicios, citas y medicamentos.',
    default_instructions: 'Siempre saluda al paciente de manera cordial. Si necesitas información adicional, pregunta de manera clara. Usa las herramientas disponibles para consultar y actualizar información del sistema.',
    reasoning_effort: 'medium',
    max_tool_calls: 10,
    temperature: 0.7,
    enable_tool_calling: true
  });
  const [saving, setSaving] = useState(false);
  const [activeTools, setActiveTools] = useState(0);

  useEffect(() => {
    fetchConfig();
    fetchActiveToolsCount();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/agent`);
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching agent config:', error);
    }
  };

  const fetchActiveToolsCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/agent-tools?status=active&limit=1`);
      setActiveTools(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching tools count:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/config/agent`, config);
      toast.success(t('agent.messages.saved'));
    } catch (error) {
      toast.error(t('agent.messages.error'));
    }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('agent.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('agent.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>{t('agent.toolCallingEnabled')}</Label>
            <p className="text-xs text-muted-foreground">{t('agent.toolCallingHint')}</p>
          </div>
          <Switch
            checked={config.enable_tool_calling}
            onCheckedChange={(checked) => setConfig({...config, enable_tool_calling: checked})}
            data-testid="tool-calling-switch"
          />
        </div>

        {config.enable_tool_calling && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <Wrench className="h-4 w-4 inline mr-2" />
              <strong>{activeTools} {t('agent.activeTools')}</strong> {t('agent.availableForAgent')}{' '}
              <a href="/agent-tools" className="text-[#E63946] underline">{t('agent.manageTools')}</a>
            </p>
          </div>
        )}

        <Separator />

        <div className="space-y-2">
          <Label>{t('agent.agentName')}</Label>
          <Input
            value={config.agent_name}
            onChange={(e) => setConfig({...config, agent_name: e.target.value})}
            placeholder={t('agent.agentNamePlaceholder')}
            data-testid="agent-name"
          />
        </div>

        <div className="space-y-2">
          <Label>{t('agent.personality')}</Label>
          <Textarea
            value={config.personality}
            onChange={(e) => setConfig({...config, personality: e.target.value})}
            placeholder={t('agent.personalityPlaceholder')}
            rows={4}
            data-testid="agent-personality"
          />
          <p className="text-xs text-muted-foreground">
            {t('agent.personalityHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label>{t('agent.defaultInstructions')}</Label>
          <Textarea
            value={config.default_instructions}
            onChange={(e) => setConfig({...config, default_instructions: e.target.value})}
            placeholder={t('agent.defaultInstructionsPlaceholder')}
            rows={3}
            data-testid="agent-instructions"
          />
          <p className="text-xs text-muted-foreground">
            {t('agent.defaultInstructionsHint')}
          </p>
        </div>

        <Separator />

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>{t('agent.reasoningEffort')}</Label>
            <Select
              value={config.reasoning_effort}
              onValueChange={(value) => setConfig({...config, reasoning_effort: value})}
            >
              <SelectTrigger data-testid="reasoning-effort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('agent.reasoningLow')}</SelectItem>
                <SelectItem value="medium">{t('agent.reasoningMedium')}</SelectItem>
                <SelectItem value="high">{t('agent.reasoningHigh')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('agent.reasoningHint')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('agent.maxToolCalls')}</Label>
            <Input
              type="number"
              min="1"
              max="50"
              value={config.max_tool_calls}
              onChange={(e) => setConfig({...config, max_tool_calls: parseInt(e.target.value) || 10})}
              data-testid="max-tool-calls"
            />
            <p className="text-xs text-muted-foreground">{t('agent.maxToolCallsHint')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('agent.temperature')}</Label>
            <Input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={config.temperature}
              onChange={(e) => setConfig({...config, temperature: parseFloat(e.target.value) || 0.7})}
              data-testid="agent-temperature"
            />
            <p className="text-xs text-muted-foreground">{t('agent.temperatureHint')}</p>
          </div>
        </div>

        <Button 
          onClick={handleSave}
          className="w-full bg-[#E63946] hover:bg-[#C92A36]"
          disabled={saving}
          data-testid="save-agent-config"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {t('agent.save')}
        </Button>
      </CardContent>
    </Card>
  );
};

// Appearance Section
const AppearanceSection = () => {
  const { t } = useTranslation('settings');
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-[#E63946]" />
          <CardTitle>{t('language.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('language.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <LanguageSelector variant="expanded" />
      </CardContent>
    </Card>
  );
};

export const SettingsPage = () => {
  const { t } = useTranslation('settings');
  const [mapsApiKey, setMapsApiKey] = useState('');

  useEffect(() => {
    // Fetch maps API key for coverage section
    const fetchMapsKey = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/geolocation/config`);
        setMapsApiKey(response.data?.api_key || '');
      } catch (error) {
        console.error('Error fetching maps config:', error);
      }
    };
    fetchMapsKey();
  }, []);
  
  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Work_Sans',sans-serif]">
          {t('title')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('subtitle')}
        </p>
      </div>

      <Tabs defaultValue="language" className="space-y-4">
        <TabsList>
          <TabsTrigger value="language" data-testid="tab-language">
            <Globe className="h-4 w-4 mr-2" />
            {t('tabs.language')}
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Brain className="h-4 w-4 mr-2" />
            {t('tabs.ai')}
          </TabsTrigger>
          <TabsTrigger value="agent" data-testid="tab-agent">
            <Bot className="h-4 w-4 mr-2" />
            {t('tabs.agent')}
          </TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows">
            <GitBranch className="h-4 w-4 mr-2" />
            {t('tabs.workflows')}
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" />
            {t('tabs.email')}
          </TabsTrigger>
          <TabsTrigger value="emailTemplates" data-testid="tab-email-templates">
            <FileText className="h-4 w-4 mr-2" />
            {t('tabs.emailTemplates')}
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <MessageSquare className="h-4 w-4 mr-2" />
            {t('tabs.whatsapp')}
          </TabsTrigger>
          <TabsTrigger value="maps" data-testid="tab-maps">
            <MapPin className="h-4 w-4 mr-2" />
            {t('tabs.maps')}
          </TabsTrigger>
          <TabsTrigger value="coverage" data-testid="tab-coverage">
            <Target className="h-4 w-4 mr-2" />
            {t('tabs.coverage')}
          </TabsTrigger>
          <TabsTrigger value="seed" data-testid="tab-seed">
            <Database className="h-4 w-4 mr-2" />
            {t('tabs.seed')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="language">
          <AppearanceSection />
        </TabsContent>
        <TabsContent value="ai">
          <AIProviderSection t={t} />
        </TabsContent>
        <TabsContent value="agent">
          <AgentSection t={t} />
        </TabsContent>
        <TabsContent value="workflows">
          <WorkflowSection t={t} />
        </TabsContent>
        <TabsContent value="email">
          <EmailProviderSection t={t} />
        </TabsContent>
        <TabsContent value="emailTemplates">
          <EmailTemplatesSection t={t} />
        </TabsContent>
        <TabsContent value="whatsapp">
          <WhatsAppSection t={t} />
        </TabsContent>
        <TabsContent value="maps">
          <MapsSection t={t} />
        </TabsContent>
        <TabsContent value="coverage">
          <CoverageMapSection mapsApiKey={mapsApiKey} />
        </TabsContent>
        <TabsContent value="seed">
          <SeedSection t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
